/**
 * TASK-074 — "held" post detection (image-first mandate).
 *
 * A post is HELD when its text generation finished but the visual failed
 * (generation error or plan limit) and no media was ever attached, so the
 * publish gate keeps it as a draft instead of posting text-only. The user
 * releases it by either attaching their own image (existing media flow) or
 * explicitly allowing a text-only publish (allow_text_only opt-in).
 *
 * Backend contract (built in parallel — TASK-050/074):
 *   - content item payloads carry `allowTextOnly: boolean` (default false);
 *   - a held post = item status 'draft' + generation finished + job
 *     visual_status = 'failed' + zero media. Note the SPA's feed mapper
 *     (ContentFeedContext.mapContentRow) upgrades such an item's display
 *     status to 'ready' (generation_status='ready' with no publish summary),
 *     so both 'draft' and 'ready' are accepted here.
 */

const HELD_ELIGIBLE_STATUSES = new Set(['draft', 'ready']);

function truthyFlag(v) {
  return v === true || v === 1 || v === '1';
}

/** Media check for a MAPPED feed row (ContentFeedContext.mapContentRow output). */
function rowHasMedia(row) {
  if (!row) return false;
  const version = row.raw?.activeVersion || null;
  return Boolean(
    row.imageUrl
    || (Array.isArray(row.imageUrls) && row.imageUrls.length > 0)
    || row.mediaPreview?.url
    || version?.imageUrl
    || (Array.isArray(version?.mediaUrls) && version.mediaUrls.length > 0)
  );
}

/**
 * Held check for a MAPPED content feed row (Library / Batch / Dashboard).
 * Mapped rows surface the job's visual_status as `visualStatus`.
 */
export function isHeldAwaitingImage(row) {
  if (!row) return false;
  const vs = String(row.visualStatus || row.raw?.job?.visualStatus || '').toLowerCase();
  if (vs !== 'failed') return false;
  const item = row.raw?.contentItem || {};
  // Explicit opt-in already given — the post is released, not held.
  if (truthyFlag(item.allowTextOnly ?? row.allowTextOnly)) return false;
  const status = String(row.status || '').toLowerCase();
  if (!HELD_ELIGIBLE_STATUSES.has(status)) return false;
  // Text generation must be finished (visual failing implies the job ran,
  // but stay defensive about in-flight rows).
  const gen = String(row.generationStage || item.generationStatus || '').toLowerCase();
  if (gen && !['ready', 'failed'].includes(gen)) return false;
  return !rowHasMedia(row);
}

/**
 * Held check for the RAW GET /api/content/:id payload (PostPreview shape:
 * { contentItem, activeVersion, job, carouselSlides, mediaUrls, ... }).
 */
export function isRawContentHeldAwaitingImage(raw) {
  if (!raw) return false;
  const item = raw.contentItem || raw;
  const vs = String(raw.job?.visualStatus || '').toLowerCase();
  if (vs !== 'failed') return false;
  if (truthyFlag(item.allowTextOnly)) return false;
  const status = String(item.status || '').toLowerCase();
  if (!HELD_ELIGIBLE_STATUSES.has(status)) return false;
  const gen = String(item.generationStatus || '').toLowerCase();
  if (gen && !['ready', 'failed'].includes(gen)) return false;
  const version = raw.activeVersion || null;
  const hasMedia = Boolean(
    version?.imageUrl
    || (Array.isArray(raw.mediaUrls) && raw.mediaUrls.length > 0)
    || (Array.isArray(raw.carouselSlides) && raw.carouselSlides.some((s) => s?.mediaAsset?.publicUrl || s?.imageUrl))
    || raw.mediaPreview?.url
  );
  return !hasMedia;
}
